import React, { useState, useEffect } from 'react';
import { CostBreakdown as CostBreakdownType, BoxSpecifications } from '../types';
import { CostBreakdownModal } from './CostBreakdownModal';
import { Eye, Save } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

import { formatPKR, formatUSD } from '../utils/formatting';
import { fetchWithInterceptors } from '../utils/apiInterceptors';
import { Card, CardContent } from './ui/Card';
import { Button } from './ui/Button';
import { Progress } from './ui/progress';
import { Slider } from './ui/slider';

interface Props {
  breakdown: CostBreakdownType;
  specs: BoxSpecifications;
  inputMode: 'dimensions' | 'openSize';
  openDimensions?: { openLength: number; openWidth: number };
  onProfitChange?: (vendorProfit: number, companyProfit?: number) => void;
  onSaveQuote?: (marginData?: {
    // Simplified margin data structure
    vendorProfit?: number;
    vendorProfitAmount?: number;
    companyProfit?: number;
    companyProfitAmount?: number;
    finalTotal?: number;
    finalCostPerUnit?: number;
  }) => void;
  // Multi-quantity selection UI
  quantityOptions?: number[];
  selectedQuantity?: number | null;
  onSelectQuantity?: (qty: number) => void;
  // Quote identification for saved quotes display
  isAutoSavedQuote?: boolean; // true for auto-saved drafts, false/undefined for manual saves
  savedQuoteData?: {
    vendorProfit?: number;
    vendorProfitAmount?: number;
    companyProfit?: number;
    companyProfitAmount?: number;
  };
  // Classic UI mode - hides profit sliders (vendor profit still applied from company settings)
  isClassicUI?: boolean;
}

export const CostBreakdownCompact: React.FC<Props> = ({
  breakdown,
  specs,
  inputMode,
  openDimensions,
  onProfitChange,
  onSaveQuote,
  quantityOptions,
  selectedQuantity,
  onSelectQuantity,
  isAutoSavedQuote = false,
  savedQuoteData,
  isClassicUI = false
}) => {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  // SIMPLIFIED MARGIN STATE MANAGEMENT
  const [vendorProfit, setVendorProfit] = useState<number>(breakdown.vendorProfit ?? specs.vendorProfit ?? 20);
  const [companyProfit, setCompanyProfit] = useState<number>(0); // Default: no company profit for auto-saved quotes
  const [agentPreferredMargin, setAgentPreferredMargin] = useState<number | null>(null);
  const [initialMarginResolved, setInitialMarginResolved] = useState(false);
  const [agentMarginInput, setAgentMarginInput] = useState<string>(String(companyProfit));

  // Extract base manufacturer profit from breakdown data (for agents to use admin settings)
  const baseManufacturerPercent = typeof breakdown.baseManufacturerProfit === 'number'
    ? breakdown.baseManufacturerProfit
    : 20;

  // Get effective profit values based on quote type
  const getEffectiveProfits = () => {
    if (isAutoSavedQuote) {
      // Auto-saved quote: vendor profit from admin settings, no company profit
      return {
        vendorProfit: savedQuoteData?.vendorProfit ?? breakdown.vendorProfit ?? 20,
        companyProfit: 0 // No company profit for auto-saved quotes
      };
    } else if (savedQuoteData) {
      // Manual saved quote: use saved values
      return {
        vendorProfit: savedQuoteData.vendorProfit ?? vendorProfit,
        companyProfit: savedQuoteData.companyProfit ?? companyProfit
      };
    } else {
      // Live calculation: prefer breakdown values (updated from parent), fallback to slider values
      return {
        vendorProfit: breakdown.vendorProfit ?? vendorProfit,
        companyProfit: breakdown.companyProfit ?? companyProfit
      };
    }
  };

  const effectiveProfits = getEffectiveProfits();

  // Check if user is admin or superadmin (not a regular agent)
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'company_admin';
  const isAgent = user?.role === 'agent';

  // Check if user can view cost breakdown details
  const canViewBreakdown = isAdmin || user?.can_view_breakdown === true;

  // Use selectedQuantity for multi-quantity mode, otherwise use specs.quantity
  const displayQuantity = selectedQuantity ?? specs.quantity;

  // Company-defined margin limits (will come from API in production)
  const [companyMarginLimits, setCompanyMarginLimits] = useState({
    min: 0,
    max: 100, // Company profit limit
    default: 0, // Default: no company profit for auto-saved quotes
    vendorProfitDefault: 20, // Default vendor profit
  });

  // Production cost calculation
  const productionCost = (typeof breakdown.productionSubtotal === 'number')
    ? breakdown.productionSubtotal
    : ((breakdown.materialCost || 0)
      + (breakdown.sheetWastageCost || 0)
      + (breakdown.printingCost || 0)
      + (breakdown.platesCost || 0)
      + (breakdown.dieCost || 0)
      + (breakdown.dieCuttingCost || 0)
      + (breakdown.pastingCost || 0)
      + (breakdown.laminationCost || 0)
      + (breakdown.laminationWastageCost || 0)
      + (breakdown.insideLaminationCost || 0)
      + (breakdown.insideLaminationWastageCost || 0)
      + (breakdown.additionalFeaturesCost || 0)
      // Insert costs (FIX: these were missing from fallback calculation)
      + (breakdown.insertMaterialCost || 0)
      + (breakdown.insertSheetWastageCost || 0)
      + (breakdown.insertPrintingCost || 0)
      + (breakdown.insertPlatesCost || 0)
      + (breakdown.insertLaminationCost || 0)
      + (breakdown.insertLaminationWastageCost || 0));

  const shippingCost = breakdown.shippingCost || 0;

  // SIMPLIFIED 2-MARGIN CALCULATION
  // Important: For agents, breakdown.total already includes base manufacturer profit

  let finalTotal, finalCostPerUnit, companyProfitAmount, vendorProfitAmount, subtotalWithVendorProfit;

  if (isAgent) {
    // For agents: Calculate base total from individual components (without double-counting agent margin)
    // This matches the logic in CostBreakdownModal
    const baseManufacturerAmount = (breakdown as CostBreakdownType & {baseManufacturerProfitAmount?: number}).baseManufacturerProfitAmount || ((productionCost * baseManufacturerPercent) / 100);
    const baseTotal = productionCost + baseManufacturerAmount + shippingCost;
    const currentCompanyProfit = effectiveProfits.companyProfit;
    companyProfitAmount = (baseTotal * currentCompanyProfit) / 100;
    finalTotal = baseTotal + companyProfitAmount;
    finalCostPerUnit = finalTotal / displayQuantity;

    // For display purposes, calculate what the vendor profit amount would be
    const currentVendorProfit = effectiveProfits.vendorProfit;
    vendorProfitAmount = (productionCost * currentVendorProfit) / 100;
    subtotalWithVendorProfit = productionCost + vendorProfitAmount;
  } else {
    // For admins: Use the original calculation logic
    // Step 1: Apply vendor profit to production cost
    const currentVendorProfit = effectiveProfits.vendorProfit;
    vendorProfitAmount = (productionCost * currentVendorProfit) / 100;
    subtotalWithVendorProfit = productionCost + vendorProfitAmount;

    // Step 2: Apply company profit to (production + vendorProfit + shipping)
    const currentCompanyProfit = effectiveProfits.companyProfit;
    const totalBeforeCompanyProfit = subtotalWithVendorProfit + shippingCost;
    companyProfitAmount = (totalBeforeCompanyProfit * currentCompanyProfit) / 100;
    finalTotal = totalBeforeCompanyProfit + companyProfitAmount;
    finalCostPerUnit = finalTotal / displayQuantity;
  }

  // Use simplified calculation results for display
  const displayTotal = finalTotal;
  const displayCostPerUnit = finalCostPerUnit;

  // Trigger parent component updates when margins change
  const handleVendorProfitChange = (newVendorProfit: number) => {
    setVendorProfit(newVendorProfit);
    onProfitChange?.(newVendorProfit, companyProfit);
  };

  const handleCompanyProfitChange = (newCompanyProfit: number) => {
    setCompanyProfit(newCompanyProfit);
    onProfitChange?.(vendorProfit, newCompanyProfit);
  };

  // For agents, show production INCLUDING base manufacturer profit (but excluding shipping)
  const productionDisplay = productionCost;

  useEffect(() => {
    if (breakdown.vendorProfit !== undefined) {
      setVendorProfit(breakdown.vendorProfit);
    } else if (specs.vendorProfit !== undefined) {
      setVendorProfit(specs.vendorProfit);
    }
  }, [breakdown.vendorProfit, specs.vendorProfit]);

  // Sync companyProfit state from breakdown when it changes (e.g., user selects agent margin)
  useEffect(() => {
    if (breakdown.companyProfit !== undefined) {
      setCompanyProfit(breakdown.companyProfit);
      setAgentMarginInput(String(breakdown.companyProfit));
    }
  }, [breakdown.companyProfit]);

  useEffect(() => {
    const loadCompanyMarginSettings = async () => {
      try {
        const resp = await fetchWithInterceptors('/api/company/agent-margin-settings');
        if (!resp.ok) {
          return;
        }
        const data = await resp.json();
        setCompanyMarginLimits(prev => {
          const parsedMin = Number(data?.minAgentMargin);
          const parsedMax = Number(data?.maxAgentMargin);
          const parsedDefault = Number(data?.defaultAgentMargin);
          const parsedBase = Number(data?.baseManufacturerProfit);

          return {
            min: Number.isFinite(parsedMin) ? parsedMin : prev.min,
            max: Number.isFinite(parsedMax) ? parsedMax : prev.max,
            default: Number.isFinite(parsedDefault) ? parsedDefault : prev.default,
            baseManufacturer: Number.isFinite(parsedBase) ? parsedBase : prev.baseManufacturer,
          };
        });
        setInitialMarginResolved(false);
      } catch (err) {
        console.error('Failed to load company margin settings:', err);
      }
    };

    loadCompanyMarginSettings();
  }, []);

  // Load agent's default margin preference from profile
  useEffect(() => {
    const loadAgentPreferences = async () => {
      if (!isAgent) return;
      try {
        const resp = await fetchWithInterceptors('/api/v1/auth/profile');
        if (resp.ok) {
          const profile = await resp.json();
          const pref = profile?.preferences?.companyProfit;
          if (typeof pref === 'number' && Number.isFinite(pref)) {
            setAgentPreferredMargin(pref);
          } else {
            setAgentPreferredMargin(null);
          }
          setInitialMarginResolved(false);
        }
      } catch (err) {
        console.error('Failed to load agent preferences:', err);
      }
    };
    loadAgentPreferences();
  }, [isAgent]);

  useEffect(() => {
    if (!isAgent || initialMarginResolved) {
      return;
    }

    const candidate = agentPreferredMargin ?? companyMarginLimits.default;
    const clamped = Math.max(companyMarginLimits.min, Math.min(companyMarginLimits.max, candidate));

    setCompanyProfit(clamped);
    setAgentMarginInput(String(clamped));
    setInitialMarginResolved(true);
  }, [isAgent, agentPreferredMargin, companyMarginLimits, initialMarginResolved]);

  // Sync agentMarginInput with companyProfit when it changes externally
  useEffect(() => {
    setAgentMarginInput(String(companyProfit));
  }, [companyProfit]);


  const handleProfitChange = (newProfit: number[]) => {
    const profit = newProfit[0];
    setVendorProfit(profit);
    // Admin sliders should NOT trigger API calls - they calculate locally only
    // The displayTotal logic in both components already uses current slider values
  };

  const handleCompanyMarginChange = (newMargin: number[]) => {
    const margin = newMargin[0];
    setCompanyProfit(margin);
    // Admin sliders should NOT trigger API calls - they calculate locally only
    // The displayTotal logic in both components already uses current slider values
  };
  
  const clampMargin = (value: number) => {
    const { min, max } = companyMarginLimits;
    if (!Number.isFinite(value)) {
      return min;
    }
    return Math.max(min, Math.min(max, value));
  };

  const handleAgentMarginChange = (newMargin: number[]) => {
    const margin = clampMargin(Math.round(newMargin[0]));
    setCompanyProfit(margin);
    setAgentMarginInput(String(margin));
  };
  
  const handleAgentMarginInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAgentMarginInput(value);
    
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      const clamped = clampMargin(numValue);
      setCompanyProfit(clamped);
    }
  };
  
  const handleAgentMarginBlur = async () => {
    const numValue = parseFloat(agentMarginInput);
    if (isNaN(numValue)) {
      setAgentMarginInput(String(companyProfit));
    } else {
      const clamped = clampMargin(numValue);
      setCompanyProfit(clamped);
      setAgentMarginInput(String(clamped));
      setAgentPreferredMargin(clamped);
      
      // Save preference to profile
      try {
        await fetchWithInterceptors('/api/v1/auth/profile', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            preferences: {
              companyProfit: clamped
            }
          })
        });
      } catch (err) {
        console.error('Failed to save agent margin preference:', err);
      }
    }
  };

  // Removed the useEffect that was causing API calls on admin profit changes
  // Admin margins should calculate instantly without backend calls

      return (
      <>
       <Card className="border-border bg-card shadow-sm h-full min-h-[500px] flex flex-col">
          <CardContent className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-card-foreground">Cost Analysis</h2>
            <div className="flex items-center gap-2">
              {quantityOptions && quantityOptions.length > 0 ? (
                <div className="relative">
                  <select
                    aria-label="Select quantity"
                    value={selectedQuantity ?? specs.quantity}
                    onChange={(e) => onSelectQuantity && onSelectQuantity(parseInt(e.target.value))}
                    className="h-8 pl-2 pr-8 rounded-md border border-border bg-background text-foreground text-sm appearance-none"
                  >
                    {quantityOptions.map((q) => (
                      <option key={q} value={q}>{q.toLocaleString()} units</option>
                    ))}
                  </select>
                  <svg className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 12a1 1 0 01-.707-.293l-4-4a1 1 0 011.414-1.414L10 9.586l3.293-3.293a1 1 0 111.414 1.414l-4 4A1 1 0 0110 12z" clipRule="evenodd" />
                  </svg>
                </div>
              ) : (
                <div className="px-3 py-1.5 bg-muted text-muted-foreground rounded-md text-sm font-medium">
                  {displayQuantity.toLocaleString()} units
                </div>
              )}
            </div>
          </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="p-4 bg-muted/50 rounded-lg border border-border">
            <div className="text-sm font-medium text-muted-foreground mb-2">Total</div>
            <div className="flex items-baseline gap-2">
              <div className="text-xl font-bold text-card-foreground truncate">{formatUSD(displayTotal)}</div>
              {isAgent && companyProfit > 0 && (
                <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                  {((companyProfit + 100) / 100).toFixed(2)}x
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground truncate">{formatPKR(displayTotal)}</div>
          </div>
          <div className="p-4 bg-muted/50 rounded-lg border border-border">
            <div className="text-sm font-medium text-muted-foreground mb-2">Per Unit</div>
            <div className="text-xl font-bold text-card-foreground truncate">{formatUSD(displayCostPerUnit)}</div>
            <div className="text-xs text-muted-foreground truncate">{formatPKR(displayCostPerUnit)}</div>
          </div>
        </div>

        {/* Cost Breakdown */}
        <div className="space-y-3 mb-4">
          <div className="p-3 bg-muted/50 rounded-md border border-border">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
                <h3 className="text-sm font-medium text-foreground/90">Production</h3>
              </div>
              <span className="text-sm font-semibold text-foreground">{formatUSD(productionDisplay)}</span>
            </div>
            <Progress value={(productionDisplay / displayTotal) * 100} className="h-1.5 bg-muted" />
            <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
              <div>Material, printing, and manufacturing costs</div>
              {isAdmin && vendorProfitAmount > 0 && (
                <div className="text-primary font-medium">
                  Includes manufacturer profit: {formatUSD(vendorProfitAmount)} ({effectiveProfits.vendorProfit}%)
                </div>
              )}
            </div>
          </div>
          
          {/* Insert Costs - Show for ALL users when insert costs exist */}
          {breakdown.insertSubtotal && breakdown.insertSubtotal > 0 && (
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-md border border-amber-200 dark:border-amber-800">
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-1 bg-amber-500 rounded-full"></div>
                  <h3 className="text-sm font-medium text-foreground/90">Insert</h3>
                </div>
                <span className="text-sm font-semibold text-amber-600">{formatUSD(breakdown.insertSubtotal)}</span>
              </div>
              <Progress value={(breakdown.insertSubtotal / displayTotal) * 100} className="h-1.5 bg-amber-100" />
              <div className="text-xs text-muted-foreground mt-1">
                Paper/cardboard insert costs
              </div>
            </div>
          )}

          {/* Admin: Show manufacturer profit and company profit separately | Agent: Show agent margin */}
          {isAdmin ? (
            <>
{/* Manufacturer Profit - Hidden for cleaner UI */}

              {effectiveProfits.companyProfit > 0 && (
                <div className="p-3 bg-muted/50 rounded-md border border-border">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
                      <h3 className="text-sm font-medium text-foreground/90">Company Profit ({effectiveProfits.companyProfit}%)</h3>
                    </div>
                    <span className="text-sm font-semibold text-foreground">{formatUSD(companyProfitAmount)}</span>
                  </div>
                  <Progress value={(companyProfitAmount / displayTotal) * 100} className="h-1.5 bg-muted" />
                  <div className="text-xs text-muted-foreground mt-1">
                    {isAutoSavedQuote ? 'Not applied to auto-saved quotes' : 'Company business profit'}
                  </div>
                </div>
              )}
            </>
          ) : isAgent ? (
            <div className="p-3 bg-muted/50 rounded-md border border-border">
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
                  <h3 className="text-sm font-medium text-foreground/90">Company Profit (Agent)</h3>
                </div>
                <span className="text-sm font-semibold text-foreground">{formatUSD(companyProfitAmount)}</span>
              </div>
              <Progress value={(companyProfitAmount / displayTotal) * 100} className="h-1.5 bg-muted" />
              <div className="text-xs text-muted-foreground mt-1">
                {companyProfit}% margin applied on total
              </div>
            </div>
          ) : null}
          
          <div className="p-3 bg-muted/50 rounded-md border border-border">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
                <h3 className="text-sm font-medium text-foreground/90">Shipping</h3>
              </div>
            <span className="text-sm font-semibold text-foreground">{formatUSD(breakdown.shippingCost || 0)}</span>
            </div>
            <Progress value={(breakdown.shippingCost / displayTotal) * 100} className="h-1.5 bg-muted" />
            <div className="text-xs text-muted-foreground mt-1">
              Delivery and logistics costs
            </div>
          </div>

          {/* Quantity Adjustment (Ganging Wastage) - Only show when there's wastage from multi-SKU orders */}
          {breakdown.gangingWastageCost && breakdown.gangingWastageCost > 0 && (
            <div className="p-3 bg-muted/50 rounded-md border border-border">
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
                  <h3 className="text-sm font-medium text-foreground/90">Quantity Adjustment</h3>
                </div>
                <span className="text-sm font-semibold text-foreground">{formatUSD(breakdown.gangingWastageCost)}</span>
              </div>
              <Progress value={(breakdown.gangingWastageCost / displayTotal) * 100} className="h-1.5 bg-muted" />
              <div className="text-xs text-muted-foreground mt-1">
                Optimization for multi-design orders
              </div>
            </div>
          )}
        </div>

        {/* Profit Sliders - Only visible to admins and only for live calculations (not saved quotes) */}
        {!isClassicUI && isAdmin && !isAutoSavedQuote && !savedQuoteData && (
          <div className="mt-auto space-y-4">
            {/* Manufacturer Profit Slider (bracket-based default, admin can override) */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-foreground/90">Manufacturer Profit</span>
                <div className="px-2 py-1 bg-muted text-foreground rounded text-xs font-medium">
                  {vendorProfit}%
                </div>
              </div>
              <Slider
                value={[vendorProfit]}
                onValueChange={handleProfitChange}
                max={50}
                min={0}
                step={1}
                className="mb-2"
              />
              <div className="text-xs text-muted-foreground">
                Bracket default: {baseManufacturerPercent}%
              </div>
            </div>

            {/* Company Margin Slider */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-foreground/90">Company Profit</span>
                <div className="px-2 py-1 bg-muted text-foreground rounded text-xs font-medium">
                  {companyProfit}%
                </div>
              </div>
              <Slider
                value={[companyProfit]}
                onValueChange={handleCompanyMarginChange}
                max={200}
                min={0}
                step={1}
                className="mb-4"
              />
            </div>
          </div>
        )}

        {/* Saved Quote Info - Show for saved quotes */}
        {isAdmin && (isAutoSavedQuote || savedQuoteData) && (
          <div className="mt-auto space-y-2">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
              <div className="flex items-center gap-2 text-xs text-blue-700 dark:text-blue-400">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">
                  {isAutoSavedQuote ? 'Auto-Saved Quote' : 'Saved Quote'}
                </span>
              </div>
              <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                {isAutoSavedQuote
                  ? 'Profit margins are fixed from company pricing settings at time of calculation'
                  : 'Profit margins are saved from when quote was created'
                }
              </div>
            </div>
          </div>
        )}
        
        {/* Agent Margin Controls - Admin Style */}
        {/* Hidden in Classic UI mode */}
        {!isClassicUI && isAgent && (
          <div className="mt-auto space-y-4">
            {/* Agent Margin Slider */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-foreground/90">Agent Margin</span>
                <div className="px-2 py-1 bg-muted text-foreground rounded text-xs font-medium">
                  {companyProfit}%
                </div>
              </div>
              <Slider
                value={[companyProfit]}
                onValueChange={handleAgentMarginChange}
                max={companyMarginLimits.max}
                min={companyMarginLimits.min}
                step={1}
                className="mb-2"
              />
            </div>

            {!canViewBreakdown && (
              <div className="text-xs text-amber-600 dark:text-amber-400 p-2 bg-amber-50 dark:bg-amber-900/20 rounded">
                ℹ️ Detailed cost breakdown is restricted. Contact your administrator for access.
              </div>
            )}
          </div>
        )}
        

        {/* Action Buttons */}
        <div className="flex gap-2.5 mt-4">
          {canViewBreakdown && (
            <Button 
              variant="outline" 
              className="flex-1 h-8 text-xs" 
              onClick={() => setIsModalOpen(true)}
            >
              <Eye className="h-3 w-3 mr-1.5" />
              View Details
            </Button>
          )}
          {onSaveQuote && (
            <Button 
              className={`${canViewBreakdown ? 'flex-1' : 'w-full'} h-8 text-xs bg-primary hover:bg-primary/90 text-primary-foreground`}
              onClick={() => {
                // Pass margin data based on user role
                if (isAgent) {
                  onSaveQuote({
                    // Agent margin data with correct field names expected by SaveQuoteDialog
                    agentMarginPercent: companyProfit,
                    agentCompanyProfit: companyProfitAmount,
                    finalTotalWithAgentMargin: displayTotal,
                    costPerUnitWithAgentMargin: displayCostPerUnit
                  });
                } else if (isAdmin) {
                  onSaveQuote({
                    // Admin margin data
                    manufacturerMarginPercent: vendorProfit,
                    companyMarginPercent: companyProfit,
                    manufacturerProfitAmount: vendorProfitAmount,
                    adminCompanyProfitAmount: companyProfitAmount,
                    adminTotalWithProfit: displayTotal,
                    adminCostPerUnitWithProfit: displayCostPerUnit
                  });
                } else {
                  onSaveQuote();
                }
              }}
            >
              <Save className="h-3 w-3 mr-1.5" />
              Save Quote
            </Button>
          )}
        </div>
        </CardContent>
      </Card>

      <CostBreakdownModal
        breakdown={breakdown}
        specs={specs}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        inputMode={inputMode}
        openDimensions={openDimensions}
        profitPercentage={isAgent ? companyProfit : vendorProfit}
        currentManufacturerProfit={isAdmin && !isAutoSavedQuote && !savedQuoteData ? vendorProfit : undefined}
        currentCompanyMargin={isAdmin && !isAutoSavedQuote && !savedQuoteData ? companyProfit : undefined}
        quantityOptions={quantityOptions}
        selectedQuantity={selectedQuantity}
        onSelectQuantity={onSelectQuantity}
        adminMarginData={isAdmin && !isAgent ? {
          manufacturerMarginPercent: vendorProfit,
          companyMarginPercent: companyProfit,
          adminCompanyProfitAmount: companyProfitAmount
        } : undefined}
        isAutoSavedQuote={isAutoSavedQuote}
        savedQuoteData={savedQuoteData}
      />
    </>
  );
};
